/**
 * 로그인 · 최초 설정 · 비밀번호 변경 강제.
 *
 * 세 화면을 한 파일에 둔다. 셋 다 "아직 콘솔에 들어오지 못한 상태"를 다루고,
 * 흐름이 서로 이어진다(설정 → 로그인 → 임시 비밀번호 변경 → 콘솔).
 *
 * 세션 토큰은 이 코드가 만지지 않는다 — 서버가 httpOnly 쿠키로 심고, 브라우저가
 * 알아서 싣는다. 자바스크립트가 읽을 수 없으니 XSS 로 새어 나갈 값 자체가 없다.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Building2, KeyRound, Loader2, MonitorPlay, ShieldCheck } from 'lucide-react';
import type { AuthState, SignageOneClient } from '../api/client.js';
import { useAction } from '../hooks.js';
import { HelpTip } from '../components/HelpTip.js';
import { PASSWORD_MIN } from '../password.js';

export function LoginScreen({
  client,
  state,
  expired = false,
  onSignedIn,
}: {
  client: SignageOneClient;
  state: AuthState;
  /** 쓰던 도중 로그인이 끊겨서 왔다. */
  expired?: boolean;
  onSignedIn: () => void;
}): ReactNode {
  return state.needs_bootstrap ? (
    <BootstrapForm client={client} onSignedIn={onSignedIn} />
  ) : (
    <SignInForm client={client} state={state} expired={expired} onSignedIn={onSignedIn} />
  );
}

function SignInForm({
  client,
  state,
  expired,
  onSignedIn,
}: {
  client: SignageOneClient;
  state: AuthState;
  expired: boolean;
  onSignedIn: () => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const action = useAction({ rawAuthMessage: true });
  const oidc = state.oidc ?? null;
  // 사내 계정만 쓰게 했으면 비밀번호 칸은 공급사 계정용이라 접어 둔다.
  const [showPassword, setShowPassword] = useState(!oidc?.enforce);
  const [ssoError] = useState(() => takeSsoError());

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void action.run(() => client.login({ email: email.trim(), password }), onSignedIn);
  };

  return (
    <Shell title="로그인" subtitle="SignageOne 사이니지 콘솔">
      <form className="flex flex-col gap-3" onSubmit={submit}>
        {ssoError ? <Alert>{ssoError}</Alert> : null}
        {oidc ? (
          <>
            {/* 서버로 이동한다 — 사내 계정 시스템을 거쳐 이 콘솔로 돌아온다(`services/oidc.ts`). */}
            <a className="btn btn-primary justify-center" href={client.mediaUrl(`/api/v1/auth/oidc/start?return_to=${encodeURIComponent(returnPath())}`)}>
              <Building2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              {oidc.label}
            </a>
            {showPassword ? (
              <p className="hint text-center">또는 아이디와 비밀번호로</p>
            ) : (
              <button type="button" className="link self-center" onClick={() => setShowPassword(true)}>
                공급사 계정으로 로그인
              </button>
            )}
          </>
        ) : null}
        {showPassword ? (
          <>
            {expired ? (
              <p className="hint" role="status">
                로그인이 끊겼습니다. 다시 로그인하세요.
                <HelpTip
                  text="다른 기기에서 전체 로그아웃했거나 계정이 비활성화·재설정되면 로그인이 끊깁니다."
                  label="로그인 끊김 도움말"
                />
              </p>
            ) : null}
            {/* 이메일이 아닌 아이디(납품 패키지의 초기 관리자 admin)도 있어 type="email" 로 막지 않는다. */}
            <Field label="아이디 또는 이메일">
              <input
                className="input"
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
                required
                autoFocus
                value={email}
                disabled={action.pending}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field label="비밀번호">
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                disabled={action.pending}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {action.error ? <Alert>{action.error}</Alert> : null}

            <SubmitButton pending={action.pending} label="로그인" pendingLabel="확인 중…" />
          </>
        ) : null}

        {state.host_identity && !oidc ? (
          <p className="hint mt-1">사내 계정 사용자는 연동 서비스에서 콘솔을 여세요.</p>
        ) : null}
        <p className="hint">
          {oidc ? '사내 계정의 비밀번호·2단계 인증은 사내 계정 시스템에서 관리합니다. ' : ''}
          비밀번호를 잊었다면 관리자에게 재설정을 요청하세요.
        </p>
      </form>
    </Shell>
  );
}

/** 사내 계정 로그인이 실패한 까닭 — 서버가 콘솔 주소에 `sso_error` 로 실어 보낸다(`routes/auth.ts`). */
export const SSO_ERROR_TEXT: Readonly<Record<string, string>> = {
  state: '로그인 요청이 만료됐거나 확인하지 못했습니다. 다시 시도하세요.',
  provider: '사내 계정 로그인이 취소됐거나 거절됐습니다.',
  token: '사내 계정 시스템과 확인하지 못했습니다. 잠시 뒤 다시 시도하고, 계속되면 관리자에게 알리세요.',
  identity: '사내 계정에 메일(아이디)이 없어 들어올 수 없습니다. 관리자에게 알리세요.',
  role: '이 서비스를 쓸 권한이 없는 사내 계정입니다. 담당자에게 권한 부여를 요청하세요.',
  vendor: '공급사 계정은 사내 계정으로 로그인할 수 없습니다. 아이디와 비밀번호로 로그인하세요.',
  disabled: '비활성화된 계정입니다. 관리자에게 문의하세요.',
  conflict: '이 메일은 다른 사내 계정에 묶여 있습니다. 관리자에게 문의하세요.',
};

/** 주소의 `sso_error` 를 꺼내 사람 말로 바꾸고, 주소에서는 지운다(새로고침에 다시 뜨지 않게). */
export function takeSsoError(location: Pick<Location, 'hash'> = window.location): string | null {
  const [path, query = ''] = location.hash.replace(/^#/, '').split('?');
  const params = new URLSearchParams(query);
  const code = params.get('sso_error');
  if (!code) return null;
  params.delete('sso_error');
  const rest = params.toString();
  if (typeof window !== 'undefined' && location === window.location) {
    window.history.replaceState(null, '', `#${path || '/'}${rest ? `?${rest}` : ''}`);
  }
  return SSO_ERROR_TEXT[code] ?? '사내 계정으로 로그인하지 못했습니다.';
}

/** 로그인 뒤 돌아갈 곳 — 지금 보던 콘솔 경로(`#` 뒤). */
function returnPath(): string {
  const path = window.location.hash.replace(/^#/, '').split('?')[0] ?? '/';
  return path.startsWith('/') ? path : '/';
}

/** 계정이 하나도 없을 때만 나온다. 서버도 같은 조건에서만 이 요청을 받는다. */
function BootstrapForm({
  client,
  onSignedIn,
}: {
  client: SignageOneClient;
  onSignedIn: () => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const action = useAction({ rawAuthMessage: true });

  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (mismatch) return;
    void action.run(
      () => client.bootstrapAccount({ email: email.trim(), name: name.trim(), password }),
      onSignedIn,
    );
  };

  return (
    <Shell title="최초 시스템 계정 만들기" subtitle="아직 계정이 없습니다" icon={ShieldCheck}>
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <Field label="이름">
          <input
            className="input"
            required
            autoFocus
            maxLength={120}
            value={name}
            disabled={action.pending}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="이메일">
          <input
            className="input"
            type="email"
            autoComplete="username"
            required
            value={email}
            disabled={action.pending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="비밀번호" help={`${PASSWORD_MIN}자 이상`}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={password}
            disabled={action.pending}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <Field label="비밀번호 확인" error={mismatch ? '비밀번호가 서로 다릅니다' : undefined}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            disabled={action.pending}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        {action.error ? <Alert>{action.error}</Alert> : null}

        <SubmitButton pending={action.pending} label="계정 만들기" pendingLabel="만드는 중…" />
        <p className="hint">
          시스템 역할(전체 권한) 계정으로 만들어집니다.
          <HelpTip
            text="고객사 담당자의 관리자 계정은 로그인 후 사용자 화면에서 추가합니다."
            label="시스템 계정 도움말"
          />
        </p>
      </form>
    </Shell>
  );
}

/**
 * 임시 비밀번호를 바꿔야 통과한다.
 *
 * 화면에서 막는 것만으로는 부족해서 서버도 다른 API 를 전부 403 으로 막는다.
 * 여기서 하는 일은 그 사실을 사람이 이해할 수 있게 보여주는 것뿐이다.
 */
export function ForcedPasswordChange({
  client,
  email,
  onChanged,
  onSignOut,
}: {
  client: SignageOneClient;
  email: string;
  onChanged: () => void;
  onSignOut: () => void;
}): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const action = useAction({ rawAuthMessage: true });

  const mismatch = confirm.length > 0 && next !== confirm;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (mismatch) return;
    void action.run(
      () => client.changePassword({ current_password: current, new_password: next }),
      onChanged,
    );
  };

  return (
    <Shell title="비밀번호 변경 필요" subtitle={email} icon={KeyRound}>
      <p className="hint mb-3">
        초기 또는 임시 비밀번호로 로그인했습니다. 비밀번호를 변경해야 콘솔을 사용할 수 있습니다.
      </p>
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <Field label="현재 비밀번호">
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={current}
            disabled={action.pending}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>
        <Field label="새 비밀번호" help={`${PASSWORD_MIN}자 이상`}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={next}
            disabled={action.pending}
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>
        <Field label="새 비밀번호 확인" error={mismatch ? '비밀번호가 서로 다릅니다' : undefined}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            disabled={action.pending}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        {action.error ? <Alert>{action.error}</Alert> : null}

        <SubmitButton pending={action.pending} label="비밀번호 변경" pendingLabel="변경 중…" />
        <button type="button" className="link mx-auto" onClick={onSignOut}>
          다른 계정으로 로그인
        </button>
      </form>
    </Shell>
  );
}

// ─────────────────────────────── 조각 ───────────────────────────────

function Shell({
  title,
  subtitle,
  icon: Icon = MonitorPlay,
  children,
}: {
  title: string;
  subtitle: string;
  icon?: typeof MonitorPlay;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex min-h-screen min-h-dvh items-center justify-center bg-th-1 p-4 sm:p-6">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-th-3">
            <Icon className="h-5 w-5 text-th-5" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</h1>
            <p className="truncate text-[12.5px] text-slate-400">{subtitle}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {help ? <small className="hint">{help}</small> : null}
      {error ? <p className="field-error">{error}</p> : null}
    </label>
  );
}

function Alert({ children }: { children: ReactNode }): ReactNode {
  return (
    <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13.5px] text-red-700" role="alert">
      {children}
    </p>
  );
}

function SubmitButton({
  pending,
  label,
  pendingLabel,
}: {
  pending: boolean;
  label: string;
  pendingLabel: string;
}): ReactNode {
  return (
    <button type="submit" className="btn btn-primary mt-1 justify-center" disabled={pending}>
      {pending ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : null}
      {pending ? pendingLabel : label}
    </button>
  );
}
